/**
 * TranscriptPanel — live rolling transcript powered by useTranscript.
 */

import React, { useEffect, useRef } from 'react';
import type { TranscriptLine } from '../hooks/useTranscript';

interface Props {
  lines:       TranscriptLine[];
  isListening: boolean;
  supported:   boolean;
  onStart:     () => void;
  onStop:      () => void;
  onClear:     () => void;
}

export const TranscriptPanel: React.FC<Props> = ({
  lines,
  isListening,
  supported,
  onStart,
  onStop,
  onClear,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [lines]);

  if (!supported) {
    return (
      <div className="transcript-panel transcript-panel--unsupported">
        <p>Live transcription is not supported in this browser.</p>
        <p>Use Chrome or Edge for Web Speech API support.</p>
      </div>
    );
  }

  return (
    <div className="transcript-panel">
      <div className="transcript-panel__header">
        <h3>
          Transcript
          {isListening && <span className="transcript-panel__live-badge"> ● LIVE</span>}
        </h3>
        <div className="transcript-panel__controls">
          {isListening ? (
            <button className="btn btn--sm btn--danger" onClick={onStop}>Stop</button>
          ) : (
            <button className="btn btn--sm btn--primary" onClick={onStart}>Start</button>
          )}
          <button className="btn btn--sm btn--ghost" onClick={onClear} disabled={lines.length === 0}>
            Clear
          </button>
        </div>
      </div>

      <div className="transcript-panel__lines">
        {lines.length === 0 ? (
          <p className="transcript-panel__empty">
            {isListening ? 'Listening…' : 'Press Start to begin transcribing'}
          </p>
        ) : (
          lines.map((line) => (
            <p
              key={line.id}
              className={`transcript-panel__line ${!line.isFinal ? 'transcript-panel__line--interim' : ''}`}
            >
              {line.text}
            </p>
          ))
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
};
